import React, { useEffect, useState } from 'react'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@bnsn/ui/components/sheet'
import { Button } from '@bnsn/ui/components/button'
import { TooltipWrapper } from '../molecules/TooltipWrapper'
import { Help } from '../atoms'
import { Settings } from 'lucide-react'

export interface SideSheetProps {
  /**
   * Whether the sheet is open
   */
  open: boolean
  /**
   * Callback when the sheet open state changes
   */
  onOpenChange: (open: boolean) => void
  /**
   * Icon to display in the header next to the headline
   */
  icon?: React.ReactNode
  /**
   * Headline text to display in the sheet header
   */
  headline: string
  /**
   * Main content to display in the sheet body
   */
  content: React.ReactNode | string
  /**
   * Footer content to display in the sheet footer
   */
  footer?: React.ReactNode | string
  /**
   * Optional help tooltip content to display next to the headline
   */
  tooltipMessage?: string
  /**
   * Side from which the sheet slides in
   * @default "right"
   */
  side?: 'left' | 'left-big' | 'right'
  /**
   * Custom className for the sheet content
   */
  className?: string
  /**
   * Custom className for the sheet header
   */
  headerClassName?: string
  /**
   * Header content for the folded (left) section
   */
  foldedHeader?: React.ReactNode | string
  /**
   * Content for the folded (left) section
   */
  foldedContent?: React.ReactNode | string
  /**
   * Footer content for the folded (left) section
   */
  foldedFooter?: React.ReactNode | string
  /**
   * Callback when the settings icon is clicked
   */
  onSettingsClick?: () => void
}

/**
 * SideSheet - A reusable sheet component that encapsulates common sheet patterns
 *
 * This component provides a standardized way to display slide-out panels with
 * consistent header styling and content structure. It's built on top of the
 * ShadCN Sheet primitive and includes support for icons, headlines, help tooltips,
 * and flexible content areas.
 *
 * @example
 * ```tsx
 * <SideSheet
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   icon={<TunerIcon />}
 *   headline="Content Tuner"
 *   tooltipMessage="Message to display in the help tooltip"
 *   content={<TunerForm />}
 *   foldedHeader={<h3>Original Content</h3>}
 *   foldedContent={<OriginalContentPanel />}
 * />
 * ```
 */
export const SideSheet: React.FC<SideSheetProps> = ({
  open,
  onOpenChange,
  icon,
  headline,
  content,
  footer,
  tooltipMessage,
  side = 'right',
  className = '!w-full sm:!w-[42rem] sm:!max-w-[50vw]',
  headerClassName,
  foldedHeader,
  foldedContent,
  foldedFooter,
  onSettingsClick,
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  useEffect(() => setShowTooltip(true), [])

  const showFoldedSection = foldedContent !== undefined

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={side} className={className}>
        {showFoldedSection && (
          <div className="flex flex-col h-full w-full bg-white dark:bg-white border-r border-gray-200 dark:border-gray-700 right-0 sm:right-full top-0 absolute z-[60] px-4">
            {foldedHeader && (
              <div className="p-0 m-0 bg-white">
                <div className="flex items-center gap-2 p-4">
                  <h2 className="text-xl font-bold text-left flex items-center gap-2 pt-2 pb-0">{foldedHeader}</h2>
                </div>
              </div>
            )}
            <div className="flex-1 overflow-auto bg-white">{foldedContent}</div>
            {foldedFooter && <div className="p-4 ">{foldedFooter}</div>}
          </div>
        )}
        <SheetHeader className={`p-0 m-0 ${headerClassName || ''}`}>
          <SheetTitle className="flex items-center justify-between gap-2 pt-0 pb-0">
            <div className="flex items-center gap-2">
              {icon && icon}
              <span className="font-bold">{headline}</span>
              {tooltipMessage && showTooltip && (
                <TooltipWrapper tooltipText={tooltipMessage}>
                  <button className="rounded-full border border-black p-1 border-black-700">
                    <Help className="text-black-700 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 w-3 h-3 " />
                  </button>
                </TooltipWrapper>
              )}
            </div>
            <div className="mr-4">
              {onSettingsClick && (
                <TooltipWrapper tooltipText="Settings">
                  <Button onClick={onSettingsClick} variant="iconOnly">
                    <Settings height={16} width={16} />
                  </Button>
                </TooltipWrapper>
              )}
            </div>
          </SheetTitle>
        </SheetHeader>
        <div className="grow overflow-y-auto">{content}</div>
        {footer && <SheetFooter className="w-full">{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  )
}

SideSheet.displayName = 'SideSheet'
